import { useState } from 'react';
import { useController } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import { Box } from '@spark-web/box';
import { Columns } from '@spark-web/columns';
import { Field } from '@spark-web/field';
import { RadioCard, RadioGroup } from '@spark-web/radio';
import type { ToggleFieldProps } from './types';

function ToggleField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description = '',
  options,
  tone,
  message,
  control,
  FieldProps,
  shouldUnregister,
  layout = 'stacked',
  isInvertedValue = false,
  ...toggleFieldProps
}: ToggleFieldProps<TFieldValues>) {
  const {
    field: { ref, onChange, value, ...field },
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    shouldUnregister,
  });

  // * On initial render, if the underlying input value is falsy, the input should display nothing
  const isFalsyValues = [undefined, null, ''].includes(value);
  const [isPristine, setIsPristine] = useState(isFalsyValues);

  const currentValue = isPristine ? '' : value ? 'true' : 'false';

  const handleChange = (selectedValue: 'true' | 'false' | '') => {
    if (isPristine) {
      setIsPristine(false);
    }

    const newValue = selectedValue === 'true' ? true : false;
    onChange(newValue);

    toggleFieldProps.onChange?.(newValue);
  };

  const yesValue = isInvertedValue ? 'false' : 'true';
  const noValue = isInvertedValue ? 'true' : 'false';

  return (
    <Field label={label} description={description} {...FieldProps}>
      <RadioGroup<'true' | 'false' | ''>
        {...toggleFieldProps}
        {...field}
        tone={error ? 'critical' : tone}
        message={error?.message ?? message}
        onChange={handleChange}
        value={currentValue}
        name={name}
      >
        <Box flex={1}>
          <Columns
            gap={layout === 'inline' ? 'small' : 'large'}
            collapseBelow={layout === 'inline' ? undefined : 'tablet'}
          >
            <RadioCard value={yesValue}>{options?.[0] ?? 'Yes'}</RadioCard>
            <RadioCard value={noValue}>{options?.[1] ?? 'No'}</RadioCard>
          </Columns>
        </Box>
      </RadioGroup>
    </Field>
  );
}

export default ToggleField;
